import { useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";
import { refusalOfProblem } from "../api/access/decisions";
import { listConnectors } from "../api/connectorsApi";
import { useIssueReport } from "../features/issueReports/issueReportContext";
import { ProjectContext } from "../projects/projectContext";
import { useLoad } from "../shared/loading/useLoad";
import { appPaths } from "../shared/routing/appPaths";
import AppButton from "../ui/buttons/AppButton";
import BlockedState from "../ui/feedback/BlockedState";
import StatusMessage from "../ui/feedback/StatusMessage";
import { describeUiError, type UiErrorDescription } from "./errorDescriptions";
import type { UiError } from "./uiError";

interface ErrorNoticeProps {
  error: UiError;
  /** Offered as "Try again" when the error is one that retrying can fix. */
  onRetry?: (() => void) | undefined;
  /** One line, for sidebars and small panes. */
  compact?: boolean;
  /** What failed, in the page's words, such as "Your PC list couldn't be loaded"; the error's own title otherwise. */
  title?: string;
  /** The page's more specific remedy when it knows the rejected operation's context. */
  message?: string;
  className?: string;
}

/**
 * The one component that shows an error to the user (NFR-5). It says what happened and
 * what to do, in the user's language, from the error's kind and code. It stays in the pane
 * that failed; the rest of the app keeps working.
 */
export default function ErrorNotice({ error, onRetry, compact = false, className, title: failedWhat, message: remedy }: ErrorNoticeProps) {
  const { t } = useTranslation();
  // A call refused for a permission one lacks says what would open it, as every refusal does (GS-8).
  const refused = error.kind === "problem" ? refusalOfProblem(error.problem.details) : null;
  if (refused?.remedy != null) {
    return (
      <div role="alert" className={className ?? (compact ? "px-2 py-3" : undefined)}>
        <BlockedState decision={refused} />
      </div>
    );
  }

  const description = describeUiError(error);
  const values = description.messageValues;
  const named = description.namedProject;
  const title = failedWhat ?? (named === undefined ? t(description.titleKey, values) : <Trans i18nKey={description.titleKey} values={values} components={{ project: <NamedProject projectId={named} /> }} />);
  const pc = description.namedPc;
  const message = remedy ?? (pc === undefined ? t(description.messageKey, values) : <NamedPcText connectorId={pc} i18nKey={description.messageKey} values={values} />);
  // A tab on an older build recovers by loading the new one (F89), never by asking again.
  const reload = error.kind === "appOutdated" ? () => { window.location.reload(); } : undefined;
  const retry = reload ?? (description.retryable ? onRetry : undefined);
  const retryLabel = t(reload === undefined ? "errors.retry" : "errors.reload");

  if (compact) {
    return (
      <div className={className ?? "px-2 py-3 text-xs"} role="alert">
        <p className="app-status-text-error font-semibold">{title}</p>
        <p className="app-text-muted">{message}</p>
        {description.technicalDetail ? <TechnicalDetail detail={description.technicalDetail} folded={description.technicalDetailFolded === true} className="app-text-muted" /> : null}
        {retry ? (
          <AppButton type="button" variant="secondary" size="xs" className="mt-2" onClick={retry}>
            {retryLabel}
          </AppButton>
        ) : null}
      </div>
    );
  }

  return (
    <div role="alert" className={className}>
      <StatusMessage tone={description.tone} title={title}>
        <p>{message}</p>
        {description.technicalDetail ? (
          <TechnicalDetail detail={description.technicalDetail} folded={description.technicalDetailFolded === true} className="mt-2 text-xs" />
        ) : null}
        {description.requestId ? <RequestReference requestId={description.requestId} /> : null}
        {retry ? (
          <AppButton type="button" variant="secondary" size="sm" className="mt-3" onClick={retry}>
            {retryLabel}
          </AppButton>
        ) : null}
      </StatusMessage>
    </div>
  );
}

/**
 * A project the text names, as the projects list has it (the web's one owner of project
 * names): a link to it where the reader may open it (P-B), its name otherwise, and "of
 * another project" outside the projects, as at sign-in. The name is the element's own text,
 * never parsed as markup (Sol B1). Only a notice that names a project reads the projects.
 */
function NamedProject({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const context = useContext(ProjectContext);
  const listed = context?.projects.status === "loaded" ? context.projects.value.find((candidate) => candidate.projectId === projectId) : undefined;
  if (context === null || listed === undefined) return <span>{t("errors.anotherProject")}</span>;
  return context.mayChoose(listed) ? <Link to={appPaths.projectSettings(listed.projectId)} className="app-link">{listed.name}</Link> : <span>{listed.name}</span>;
}

/**
 * A text that names the PC a refusal happened on as `{{pc}}`: by its name where it is one of
 * the reader's own, online or not, as the Thread's hosting line names it; "the host's PC"
 * otherwise, since another person's PC stays unnamed (TA-4). Only a notice that names a PC
 * reads the reader's connectors.
 */
function NamedPcText({ connectorId, i18nKey, values }: { connectorId: string; i18nKey: string; values: UiErrorDescription["messageValues"] }) {
  const { t } = useTranslation();
  const { state } = useLoad(readOwnConnectors);
  const own = state.status === "loaded" ? state.value.find((connector) => connector.connectorId === connectorId) : undefined;
  return <>{t(i18nKey, { ...values, pc: own?.name ?? t("threads.toolApproval.hostPc") })}</>;
}

const readOwnConnectors = (signal: AbortSignal) => listConnectors(signal);

/**
 * The server's own explanation, in English. A technical one, such as a tool's own message,
 * is folded behind Details: it is there for whoever needs it, not in the main text (P-C).
 */
function TechnicalDetail({ detail, folded, className }: { detail: string; folded: boolean; className: string }) {
  const { t } = useTranslation();
  const text = <p className="break-words font-mono">{folded ? detail : t("errors.technicalDetail", { detail })}</p>;
  if (!folded) return <div className={className}>{text}</div>;
  return (
    <details className={className}>
      <summary className="cursor-pointer">{t("errors.details")}</summary>
      <div className="mt-1">{text}</div>
    </details>
  );
}

/** The request's reference, as Sentry's and GitHub's error pages show it: in the working app
 * a link opens "Report a problem" with the reference filled in (F13); elsewhere, such as
 * sign-in, the user is asked to quote it. */
function RequestReference({ requestId }: { requestId: string }) {
  const { t } = useTranslation();
  const openIssueReport = useIssueReport();
  if (openIssueReport === null) {
    return <p className="mt-2 text-xs">{t("errors.requestReference", { requestId })}</p>;
  }
  return (
    <p className="mt-2 text-xs">
      <AppButton variant="secondary" size="xs" onClick={() => { openIssueReport({ reference: requestId }); }}>
        {t("errors.reportThis")}
      </AppButton>
      {" · "}
      <span className="break-all font-mono">{t("errors.referenceLabel", { requestId })}</span>
    </p>
  );
}
